import { useMemo } from 'react';

import { compareAppVersions } from '@/utils/compare-app-versions';
import { getAppVersion } from '@/utils/get-app-version';

import { WhatsNewCardData, useWhatsNewData } from './use-whats-new-data';
import { useWhatsNewStore } from './use-whats-new-store';

export type WhatsNewLobbyCardData = {
    title: string | null | undefined;
    description: string | null | undefined;
    buttonLabel: string | null | undefined;
    image: string | null | undefined;
};

export const isCardVisible = (
    card: WhatsNewCardData,
    seenCardIds: string[],
    currentVersion: string | undefined
): boolean => {
    if (seenCardIds.includes(card.id)) return false;
    const introducedInVersion = card.data.introduced_in_version;
    // skip cards introduced in a version newer than the current app version
    if (introducedInVersion && currentVersion && compareAppVersions(currentVersion, introducedInVersion) === 'less') {
        return false;
    }
    return true;
};

export const useWhatsNewVisibility = () => {
    const { data } = useWhatsNewData();

    const seenCardIds = useWhatsNewStore(state => state.seenCardIds);

    const hasUnseen = useMemo(() => {
        const currentVersion = getAppVersion();
        return (data?.cards ?? []).some(card => isCardVisible(card, seenCardIds, currentVersion));
    }, [data, seenCardIds]);

    const lobbyCardData = useMemo<WhatsNewLobbyCardData>(
        () => ({
            title: data?.lobby_card_title,
            description: data?.lobby_card_description,
            buttonLabel: data?.lobby_card_button_label,
            image: data?.lobby_card_image?.url,
        }),
        [data]
    );

    return {
        hasUnseen,
        lobbyCardData,
    };
};
